import React, { useState, useRef, useCallback } from 'react'
import PropTypes from 'prop-types'
import { formatDate } from 'Shared/helpers'

const t = (key, opts) => !window.I18n ? '' : window.I18n.t(key, opts)

const H = 200
const PAD = { top: 16, right: 20, bottom: 30, left: 40 }
const INNER_H = H - PAD.top - PAD.bottom
const DEFAULT_W = 600

const scale = (value, min, max, size) =>
  max === min ? size / 2 : ((value - min) / (max - min)) * size

const computePoints = (data, key, minVal, maxVal, innerW) =>
  data.map((d, i) => ({
    x: PAD.left + scale(i, 0, Math.max(data.length - 1, 1), innerW),
    y: PAD.top + INNER_H - scale(d[key], minVal, maxVal, INNER_H),
  }))

const gradients = (pts) =>
  pts.map((pt, i) => {
    if (i === 0) return (pts[1].y - pt.y) / (pts[1].x - pt.x)
    if (i === pts.length - 1) return (pt.y - pts[i - 1].y) / (pt.x - pts[i - 1].x)
    return (pts[i - 1].y - pts[i + 1].y) / (pts[i - 1].x - pts[i + 1].x)
  })

const toSmoothPath = (pts) => {
  if (pts.length < 2) return ''
  if (pts.length === 2) return `M${pts[0].x},${pts[0].y} L${pts[1].x},${pts[1].y}`

  const g = gradients(pts)
  let d = `M${pts[0].x},${pts[0].y}`
  for (let i = 1; i < pts.length; i++) {
    const prev = pts[i - 1]
    const curr = pts[i]
    const ix = (curr.x - prev.x) / 4
    const x1 = prev.x + ix
    const y1 = prev.y + ix * g[i - 1]
    const x2 = curr.x - ix
    const y2 = curr.y - ix * g[i]
    d += ` C${x1},${y1} ${x2},${y2} ${curr.x},${curr.y}`
  }
  return d
}

const gridTicks = (min, max, count = 5) => {
  const step = (max - min) / (count - 1)
  return Array.from({ length: count }, (_, i) => Math.round(min + i * step))
}


const HandicapHistoryChart = ({ history, years }) => {
  const [selectedYear, setSelectedYear] = useState(years.length > 0 ? years[years.length - 1] : null)
  const [hover, setHover] = useState(null)
  const svgRef = useRef(null)
  const containerRef = useRef(null)

  // Render the SVG at its real pixel width (viewBox width == rendered width)
  // so the coordinate system is 1:1. This keeps font sizes, stroke widths and
  // dots at a constant on-screen size no matter how wide the chart is drawn.
  const [width, setWidth] = useState(DEFAULT_W)
  const observerRef = useRef(null)
  const setContainer = useCallback((node) => {
    if (observerRef.current) {
      observerRef.current.disconnect()
      observerRef.current = null
    }
    containerRef.current = node
    if (!node) return

    const measure = () => { if (node.clientWidth > 0) setWidth(node.clientWidth) }
    measure()
    if (typeof ResizeObserver !== 'undefined') {
      observerRef.current = new ResizeObserver(measure)
      observerRef.current.observe(node)
    }
  }, [])

  if (!history || history.length === 0) return null

  const filtered = selectedYear ? history.filter(p => p.year === selectedYear) : history
  if (filtered.length === 0) return null

  const innerW = width - PAD.left - PAD.right

  const allVals = filtered.flatMap(p => [p.player_hi, p.club_average].filter(v => v != null))
  // No numeric points in the selected year (all values null): bail before the
  // Math.min/max of an empty array yields ±Infinity and every coordinate is NaN.
  if (allVals.length === 0) return null
  const minVal = Math.floor(Math.min(...allVals))
  const maxVal = Math.ceil(Math.max(...allVals))

  const playerPts = computePoints(filtered, 'player_hi', minVal, maxVal, innerW)
  const clubFiltered = filtered.filter(p => p.club_average != null)
  const clubPts = computePoints(clubFiltered, 'club_average', minVal, maxVal, innerW)

  const playerPath = toSmoothPath(playerPts)
  const clubPath   = toSmoothPath(clubPts)
  const xLabels = filtered.filter((_, i) => i % Math.max(1, Math.floor(filtered.length / 6)) === 0)
  // Dedupe: a small HI range rounds to repeated tick values, which would
  // otherwise produce duplicate gridlines/labels.
  const ticks = Array.from(new Set(gridTicks(minVal, maxVal)))

  // Snap to the point nearest the mouse X (the SVG is drawn 1:1, but scale by
  // the rendered width in case the container rounds it).
  const handleHoverMove = (e) => {
    const svg = svgRef.current
    const container = containerRef.current
    if (!svg || !container) return
    const svgRect = svg.getBoundingClientRect()
    if (!svgRect.width) return
    const scaleX = svgRect.width / width
    const localX = (e.clientX - svgRect.left) / scaleX
    const step = innerW / Math.max(filtered.length - 1, 1)
    const idx = Math.max(0, Math.min(filtered.length - 1, Math.round((localX - PAD.left) / step)))
    // Tooltip X is pinned to the crosshair (nearest point); only Y follows the cursor.
    const cRect = container.getBoundingClientRect()
    const lineX = (svgRect.left - cRect.left) + playerPts[idx].x * scaleX
    setHover({ idx, lineX, y: e.clientY - cRect.top })
  }

  return (
    <div className='handicap-history-chart' ref={setContainer}>
      <div className='handicap-history-chart__header'>
        <div className='mhh-switcher'>
          {years.map(y => (
            <button key={y} type='button' onClick={() => setSelectedYear(y)} className={`segment-btn${selectedYear === y ? ' segment-btn--active' : ''}`}>
              {y}
            </button>
          ))}
        </div>
        <span className='handicap-history-chart__legend'>
          <span className='handicap-history-chart__legend-item'>
            <span className='handicap-history-chart__dot handicap-history-chart__dot--player' />
            {t('member_handicap_history.chart.player_handicap')}
          </span>
          <span className='handicap-history-chart__legend-item'>
            <span className='handicap-history-chart__dot handicap-history-chart__dot--average' />
            {t('member_handicap_history.chart.average_handicap')}
          </span>
        </span>
      </div>
      <svg ref={svgRef} height={H} viewBox={`0 0 ${width} ${H}`} className='handicap-history-chart__svg'>
        {ticks.map((v, i) => {
          const y = PAD.top + INNER_H - scale(v, minVal, maxVal, INNER_H)
          return (
            <g key={i}>
              <line x1={PAD.left} x2={width - PAD.right} y1={y} y2={y} className='chart-grid-line' />
              <text x={PAD.left - 6} y={y + 4} className='chart-grid-text'>{v}</text>
            </g>
          )
        })}
        {xLabels.map((d, i) => {
          const idx = filtered.indexOf(d)
          const x = PAD.left + scale(idx, 0, Math.max(filtered.length - 1, 1), innerW)
          return (
            <text key={i} x={x} y={H - 4} className='chart-x-label'>
              {d.date ? formatDate(d.date, null, 'MMM D') : ''}
            </text>
          )
        })}
        {playerPts.length > 1 && (
          <path d={playerPath} className='chart-player-path' />
        )}
        {clubPts.length > 1 && (
          <path d={clubPath} className='chart-club-path' />
        )}
        {hover && playerPts[hover.idx] && (
          <line
            x1={playerPts[hover.idx].x}
            x2={playerPts[hover.idx].x}
            y1={PAD.top}
            y2={PAD.top + INNER_H}
            className='chart-crosshair'
          />
        )}
        {clubPts.map((pt, i) => (
          <circle key={`c-${i}`} cx={pt.x} cy={pt.y} r={3} className='chart-club-dot' />
        ))}
        {playerPts.map((pt, i) => (
          <circle key={`p-${i}`} cx={pt.x} cy={pt.y} r={hover && hover.idx === i ? 5 : 3} className='chart-player-dot' />
        ))}
        {/* Full-width overlay: hovering anywhere snaps the crosshair to the nearest point */}
        <rect
          x={PAD.left}
          y={PAD.top}
          width={Math.max(innerW, 0)}
          height={INNER_H}
          fill='transparent'
          className='handicap-history-chart__hit'
          onMouseMove={handleHoverMove}
          onMouseLeave={() => setHover(null)}
        />
      </svg>
      {hover && filtered[hover.idx] && (
        <div
          className={`handicap-history-chart__tooltip${hover.lineX > width - 180 ? ' handicap-history-chart__tooltip--left' : ''}`}
          style={{ left: `${hover.lineX}px`, top: `${hover.y}px` }}
        >
          <div className='handicap-history-chart__tooltip-date'>
            {formatDate(filtered[hover.idx].date, null, 'MMM D, YYYY')}
          </div>
          <div className='handicap-history-chart__tooltip-player'>
            {t('member_handicap_history.chart.player_handicap')} : {Number(filtered[hover.idx].player_hi).toFixed(1)}
          </div>
          {filtered[hover.idx].club_average != null && (
            <div className='handicap-history-chart__tooltip-avg'>
              {t('member_handicap_history.chart.average_handicap')} : {Number(filtered[hover.idx].club_average).toFixed(1)}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

HandicapHistoryChart.propTypes = {
  history: PropTypes.arrayOf(PropTypes.shape({
    date: PropTypes.string,
    player_hi: PropTypes.number,
    club_average: PropTypes.number,
    year: PropTypes.number,
  })),
  years: PropTypes.arrayOf(PropTypes.number),
}

export default HandicapHistoryChart
